import type { Provider, User } from "@supabase/supabase-js";
import { supabase, supabaseUrl } from "./supabaseClient";
import {
  clearPasswordRecoveryStorage,
  passwordRecoveryClient,
  passwordVerificationClient,
} from "./passwordAuthClients";

export { supabase };

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  level: string;
  interests: string[];
  targetTier: string;
  xp: number;
  streakDays: number;
}

export interface PasswordAccountStatus {
  hasEmailPassword: boolean;
}

export interface RecoveryAuthorization {
  userId: string;
  sessionToken: string;
}

export class EmailConfirmationRequiredError extends Error {
  constructor() {
    super("Check your email to finish creating your account.");
    this.name = "EmailConfirmationRequiredError";
  }
}

export class InvalidPasswordRecoveryError extends Error {
  constructor() {
    super("This password reset link is invalid or has expired.");
    this.name = "InvalidPasswordRecoveryError";
  }
}

export class PasswordManagedByProviderError extends Error {
  constructor() {
    super("This account's password is managed by its sign-in provider.");
    this.name = "PasswordManagedByProviderError";
  }
}

export class PasswordRecoveryCleanupError extends Error {
  readonly cleanupCause: unknown;
  readonly passwordUpdated: boolean;

  constructor(cleanupCause?: unknown, passwordUpdated = false) {
    super("Could not securely clear the password recovery authorization.");
    this.name = "PasswordRecoveryCleanupError";
    this.cleanupCause = cleanupCause;
    this.passwordUpdated = passwordUpdated;
  }
}

export class PasswordVerificationCleanupError extends Error {
  readonly cleanupCause: unknown;
  readonly passwordUpdated = true;

  constructor(cleanupCause?: unknown) {
    super(
      "Password changed, but the temporary verification session could not be cleared."
    );
    this.name = "PasswordVerificationCleanupError";
    this.cleanupCause = cleanupCause;
  }
}

type ProfileUpdates = {
  level: string;
  interests: string[];
  target_tier: string;
};

async function getFunctionErrorMessage(
  error: any,
  fallback: string
): Promise<string> {
  if (error?.context?.status === 404) {
    return `The deleteAccount Edge Function is not available on ${supabaseUrl}. Deploy it with supabase functions deploy deleteAccount and try again.`;
  }

  if (error?.name === "FunctionsFetchError") {
    return "Could not reach the deleteAccount Edge Function. Check that your Supabase URL is correct and the function is running.";
  }

  try {
    if (error?.context?.json) {
      const body = await error.context.json();
      return body?.message || body?.error || fallback;
    }
  } catch {
    return fallback;
  }

  return error?.message || fallback;
}

function getDisplayNameFromAuthUser(user: User) {
  return (
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.email?.split("@")[0] ||
    "User"
  );
}

function getEmailFromAuthUser(user: User) {
  return user.email || "";
}

function hasEmailPassword(user: User): boolean {
  const identityProviders = user.identities?.map(({ provider }) => provider) ?? [];
  const metadataProviders = Array.isArray(user.app_metadata?.providers)
    ? user.app_metadata.providers
    : [];

  return (
    identityProviders.includes("email") ||
    metadataProviders.includes("email") ||
    user.app_metadata?.provider === "email"
  );
}

function toUserProfile(userProfile: any, authUser: User): UserProfile {
  return {
    id: userProfile.id,
    name: userProfile.name || getDisplayNameFromAuthUser(authUser),
    email: getEmailFromAuthUser(authUser),
    level: userProfile.level || "A1",
    interests: userProfile.interests || [],
    targetTier: userProfile.target_tier || "A1",
    xp: userProfile.xp || 0,
    streakDays: userProfile.streak_days || 0,
  };
}

function getOAuthOptions(provider: Provider) {
  const options: {
    redirectTo: string;
    queryParams?: Record<string, string>;
  } = {
    redirectTo: `${window.location.origin}/auth/callback`,
  };

  if (provider === "google") {
    options.queryParams = {
      access_type: "offline",
      prompt: "consent",
    };
  }

  return options;
}

async function fetchProfile(authUser: User) {
  const { data, error } = await supabase
    .from("users")
    .select("*")
    .eq("id", authUser.id)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to load user profile: ${error.message}`);
  }

  return data;
}

async function ensureProfile(
  authUser: User,
  profileDefaults: Partial<Pick<UserProfile, "name" | "interests" | "level" | "targetTier">> = {}
): Promise<UserProfile> {
  const existingProfile = await fetchProfile(authUser);
  if (existingProfile) {
    return toUserProfile(existingProfile, authUser);
  }

  const insertPayload = {
    id: authUser.id,
    name: profileDefaults.name || getDisplayNameFromAuthUser(authUser),
    level: profileDefaults.level || "A1",
    interests: profileDefaults.interests || [],
    target_tier: profileDefaults.targetTier || "A1",
    xp: 0,
    streak_days: 0,
  };

  const { data, error } = await supabase
    .from("users")
    .insert(insertPayload)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create user profile: ${error.message}`);
  }

  return toUserProfile(data, authUser);
}

export class AuthService {
  static async requestPasswordReset(email: string): Promise<void> {
    clearPasswordRecoveryStorage();
    try {
      const { error } =
        await passwordRecoveryClient.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });

      if (error) {
        throw new Error(error.message || "Could not send password reset email");
      }
    } catch (error) {
      clearPasswordRecoveryStorage();
      throw error;
    }
  }

  static async clearPasswordRecoveryAuthorization(): Promise<void> {
    let cleanupError: unknown;

    try {
      const { error } = await passwordRecoveryClient.auth.signOut({
        scope: "local",
      });
      cleanupError = error;
    } catch (error) {
      cleanupError = error;
    } finally {
      clearPasswordRecoveryStorage();
    }

    if (cleanupError) {
      throw new PasswordRecoveryCleanupError(cleanupError);
    }
  }

  private static async rejectPasswordRecovery(
    error: InvalidPasswordRecoveryError | PasswordManagedByProviderError
  ): Promise<never> {
    await AuthService.clearPasswordRecoveryAuthorization();
    throw error;
  }

  static async exchangePasswordRecoveryCode(
    code: string
  ): Promise<RecoveryAuthorization> {
    if (!code.trim()) {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    let exchangeResult;
    try {
      exchangeResult =
        await passwordRecoveryClient.auth.exchangeCodeForSession(code);
    } catch {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    const { data, error } = exchangeResult;
    if (
      error ||
      !data.session ||
      !data.user ||
      !data.session.access_token ||
      data.session.user.id !== data.user.id
    ) {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    if (!hasEmailPassword(data.user)) {
      return AuthService.rejectPasswordRecovery(
        new PasswordManagedByProviderError()
      );
    }

    return {
      userId: data.user.id,
      sessionToken: data.session.access_token,
    };
  }

  static async getCurrentUserProfile(): Promise<UserProfile | null> {
    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();

    if (sessionError) {
      throw sessionError;
    }

    if (!sessionData.session) {
      return null;
    }

    const { data, error } = await supabase.auth.getUser();
    if (error) {
      throw error;
    }

    if (!data.user) {
      return null;
    }

    return ensureProfile(data.user);
  }

  static async loginWithEmail(
    email: string,
    password: string
  ): Promise<UserProfile> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(error.message || "Invalid email or password");
    }

    if (!data.user) {
      throw new Error("No authenticated user returned");
    }

    return ensureProfile(data.user);
  }

  static async signupWithEmail(
    name: string,
    email: string,
    password: string,
    interests: string[]
  ): Promise<UserProfile> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          full_name: name,
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    if (!data.user || !data.session) {
      throw new EmailConfirmationRequiredError();
    }

    return ensureProfile(data.user, {
      name,
      interests,
    });
  }

  static async loginWithProvider(provider: Provider): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: getOAuthOptions(provider),
    });

    if (error) {
      throw error;
    }
  }

  static async loginWithGoogle(): Promise<void> {
    return AuthService.loginWithProvider("google");
  }

  static async handleAuthCallback(): Promise<UserProfile | null> {
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        throw error;
      }
    }

    return AuthService.getCurrentUserProfile();
  }

  static async updateUserProfile(
    userId: string,
    updates: ProfileUpdates
  ): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        return { data: null, error: userError };
      }

      if (!userData.user) {
        return { data: null, error: new Error("No authenticated user found") };
      }

      const authUser = userData.user;
      await ensureProfile(authUser);

      if (userId !== authUser.id) {
        console.warn(
          "Ignoring stale onboarding profile id and updating the authenticated profile instead."
        );
      }

      const { data, error } = await supabase
        .from("users")
        .update({
          level: updates.level,
          interests: updates.interests,
          target_tier: updates.target_tier,
        })
        .eq("id", authUser.id)
        .select()
        .maybeSingle();

      if (error) {
        return { data: null, error };
      }

      if (!data) {
        return {
          data: null,
          error: new Error("No matching user profile was updated"),
        };
      }

      return { data: toUserProfile(data, authUser), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async getPasswordAccountStatus(): Promise<PasswordAccountStatus> {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw new Error(error?.message || "No authenticated user found");
    }

    return { hasEmailPassword: hasEmailPassword(data.user) };
  }

  static async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    const { data, error } = await supabase.auth.getUser();
    const user = data.user;
    if (error || !user?.email) {
      throw new Error(error?.message || "No authenticated email account found");
    }
    if (!hasEmailPassword(user)) {
      throw new PasswordManagedByProviderError();
    }

    const { data: verificationData, error: verificationError } =
      await passwordVerificationClient.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
    if (
      verificationError ||
      !verificationData.user ||
      !verificationData.session
    ) {
      throw new Error(
        verificationError?.message || "Current password is incorrect"
      );
    }

    let passwordChangeError: Error | null = null;

    const verifiedUserMatches =
      verificationData.user.id === user.id &&
      verificationData.session.user.id === user.id;
    if (!verifiedUserMatches) {
      passwordChangeError = new Error(
        "Current password verification did not match the authenticated user"
      );
    } else {
      try {
        const { error: updateError } =
          await passwordVerificationClient.auth.updateUser({
            password: newPassword,
          });
        if (updateError) {
          passwordChangeError = new Error(
            updateError.message || "Could not update password"
          );
        }
      } catch (updateError) {
        passwordChangeError =
          updateError instanceof Error
            ? updateError
            : new Error("Could not update password");
      }
    }

    let verificationCleanupError: Error | null = null;
    try {
      const { error } = await passwordVerificationClient.auth.signOut({
        scope: "local",
      });
      if (error) {
        verificationCleanupError = new Error(
          error.message || "Could not end password verification session"
        );
      }
    } catch (cleanupError) {
      verificationCleanupError =
        cleanupError instanceof Error
          ? cleanupError
          : new Error("Could not end password verification session");
    }

    if (passwordChangeError) {
      throw passwordChangeError;
    }
    if (verificationCleanupError) {
      throw new PasswordVerificationCleanupError(verificationCleanupError);
    }
  }

  static async updateRecoveredPassword(
    authorization: RecoveryAuthorization,
    newPassword: string
  ): Promise<void> {
    let sessionResult;
    try {
      sessionResult = await passwordRecoveryClient.auth.getSession();
    } catch {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    const activeSession = sessionResult.data.session;
    const sessionMatches =
      !sessionResult.error &&
      !!activeSession &&
      !!authorization.userId &&
      !!authorization.sessionToken &&
      activeSession.user.id === authorization.userId &&
      activeSession.access_token === authorization.sessionToken;
    if (!sessionMatches) {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    const { data: verifiedData, error: verificationError } =
      await passwordRecoveryClient.auth.getUser(authorization.sessionToken);
    if (
      verificationError ||
      !verifiedData.user ||
      verifiedData.user.id !== authorization.userId
    ) {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }
    if (!hasEmailPassword(verifiedData.user)) {
      return AuthService.rejectPasswordRecovery(
        new PasswordManagedByProviderError()
      );
    }

    const { data: currentSessionData, error: currentSessionError } =
      await passwordRecoveryClient.auth.getSession();
    if (
      currentSessionError ||
      !currentSessionData.session ||
      currentSessionData.session.user.id !== authorization.userId ||
      currentSessionData.session.access_token !== authorization.sessionToken
    ) {
      return AuthService.rejectPasswordRecovery(
        new InvalidPasswordRecoveryError()
      );
    }

    const { error: updateError } =
      await passwordRecoveryClient.auth.updateUser({
        password: newPassword,
      });
    if (updateError) {
      throw new Error(updateError.message || "Could not update password");
    }

    try {
      await AuthService.clearPasswordRecoveryAuthorization();
    } catch (cleanupError) {
      if (cleanupError instanceof PasswordRecoveryCleanupError) {
        throw new PasswordRecoveryCleanupError(
          cleanupError.cleanupCause,
          true
        );
      }
      throw cleanupError;
    }
  }

  static async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  }

  static async deleteAccount(): Promise<void> {
    const { error } = await supabase.functions.invoke("deleteAccount", {
      body: {},
    });

    if (error) {
      throw new Error(
        await getFunctionErrorMessage(error, "Failed to delete account")
      );
    }

    const { error: signOutError } = await supabase.auth.signOut({
      scope: "global",
    });
    if (signOutError) {
      console.warn(
        "Account deleted, but local sign-out reported an error:",
        signOutError
      );
    }
  }
}
